export class OHIFViewer {
  constructor(elementId, config = {}) {
    this.elementId = elementId;
    this.config = this.buildConfig(config);
    this.viewerInstance = null;
    this.isInitialized = false;
  }

  buildConfig(userConfig) {
    const defaultConfig = {
      routerBasename: '/viewer',
      showStudyList: false,
      disableEditing: false,
      enableGoogleCloudAdapter: false,
      servers: {
        dicomWeb: [
          {
            name: 'Orthanc',
            wadoUriRoot: userConfig.orthancUrl || 'http://localhost:8042/wado',
            qidoRoot: userConfig.orthancUrl || 'http://localhost:8042/dicom-web',
            wadoRoot: userConfig.orthancUrl || 'http://localhost:8042/dicom-web',
            qidoSupportsIncludeField: true,
            imageRendering: 'wadors',
            thumbnailRendering: 'wadors',
            requestOptions: {
              headers: userConfig.headers || {},
            },
          },
        ],
      },
      extensions: [],
      modes: [],
      customizationService: {
        // Customizações baseadas no tipo de usuário
        customizations: this.getCustomizationsByRole(userConfig.userRole),
      },
      hotkeys: [
        { commandName: 'incrementActiveViewport', label: 'Next Viewport', keys: ['right'] },
        { commandName: 'decrementActiveViewport', label: 'Previous Viewport', keys: ['left'] },
        { commandName: 'rotateViewportCW', label: 'Rotate Right', keys: ['r'] },
        { commandName: 'rotateViewportCCW', label: 'Rotate Left', keys: ['l'] },
        { commandName: 'invertViewport', label: 'Invert', keys: ['i'] },
        { commandName: 'flipViewportVertical', label: 'Flip Horizontally', keys: ['h'] },
        { commandName: 'flipViewportHorizontal', label: 'Flip Vertically', keys: ['v'] },
        { commandName: 'scaleUpViewport', label: 'Zoom In', keys: ['+'] },
        { commandName: 'scaleDownViewport', label: 'Zoom Out', keys: ['-'] },
        { commandName: 'fitViewportToWindow', label: 'Zoom to Fit', keys: ['='] },
        { commandName: 'resetViewport', label: 'Reset', keys: ['space'] },
        { commandName: 'nextImage', label: 'Next Image', keys: ['down'] },
        { commandName: 'previousImage', label: 'Previous Image', keys: ['up'] },
      ],
    };

    return { ...defaultConfig, ...userConfig };
  }

  getCustomizationsByRole(userRole) {
    const baseCustomizations = {
      // Customizações básicas para todos os usuários
      cornerstoneOverlayTopLeft: {
        id: 'cornerstoneOverlayTopLeft',
        items: [
          {
            id: 'PatientNameOverlay',
            customizationType: 'ohif.overlayItem.patientName',
          },
          {
            id: 'PatientSexOverlay',
            customizationType: 'ohif.overlayItem.patientSex',
          },
          {
            id: 'PatientAgeOverlay',
            customizationType: 'ohif.overlayItem.patientAge',
          },
        ],
      },
      cornerstoneOverlayTopRight: {
        id: 'cornerstoneOverlayTopRight',
        items: [
          {
            id: 'StudyDateOverlay',
            customizationType: 'ohif.overlayItem.studyDate',
          },
          {
            id: 'SeriesDescriptionOverlay',
            customizationType: 'ohif.overlayItem.seriesDescription',
          },
        ],
      },
    };

    // Customizações específicas por role
    const roleCustomizations = {
      technician: {
        // Técnicos têm funcionalidades limitadas
        primaryToolbar: {
          id: 'primaryToolbar',
          items: [
            {
              id: 'MeasurementTools',
              type: 'ohif.splitButton',
              props: {
                groupId: 'MeasurementTools',
                isRadio: true,
                primary: {
                  id: 'Length',
                  icon: 'tool-length',
                  label: 'Length',
                  tooltip: 'Length Tool',
                  commandName: 'setToolActive',
                  commandOptions: { toolName: 'Length' },
                },
                secondary: {
                  id: 'secondaryMeasurementTools',
                  icon: 'chevron-down',
                  tooltip: 'More Measurement Tools',
                },
                items: [
                  {
                    id: 'Length',
                    icon: 'tool-length',
                    label: 'Length',
                    tooltip: 'Length Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'Length' },
                  },
                  {
                    id: 'Bidirectional',
                    icon: 'tool-bidirectional',
                    label: 'Bidirectional',
                    tooltip: 'Bidirectional Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'Bidirectional' },
                  },
                ],
              },
            },
            {
              id: 'Zoom',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-zoom',
                label: 'Zoom',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'Zoom' },
              },
            },
            {
              id: 'WindowLevel',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-window-level',
                label: 'Window Level',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'WindowLevel' },
              },
            },
            {
              id: 'Pan',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-move',
                label: 'Pan',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'Pan' },
              },
            },
          ],
        },
      },
      doctor: {
        // Médicos têm acesso completo às ferramentas de medição e análise
        primaryToolbar: {
          id: 'primaryToolbar',
          items: [
            {
              id: 'MeasurementTools',
              type: 'ohif.splitButton',
              props: {
                groupId: 'MeasurementTools',
                isRadio: true,
                primary: {
                  id: 'Length',
                  icon: 'tool-length',
                  label: 'Length',
                  tooltip: 'Length Tool',
                  commandName: 'setToolActive',
                  commandOptions: { toolName: 'Length' },
                },
                secondary: {
                  id: 'secondaryMeasurementTools',
                  icon: 'chevron-down',
                  tooltip: 'More Measurement Tools',
                },
                items: [
                  {
                    id: 'Length',
                    icon: 'tool-length',
                    label: 'Length',
                    tooltip: 'Length Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'Length' },
                  },
                  {
                    id: 'Bidirectional',
                    icon: 'tool-bidirectional',
                    label: 'Bidirectional',
                    tooltip: 'Bidirectional Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'Bidirectional' },
                  },
                  {
                    id: 'EllipticalROI',
                    icon: 'tool-elipse',
                    label: 'Ellipse',
                    tooltip: 'EllipticalROI Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'EllipticalROI' },
                  },
                  {
                    id: 'CircleROI',
                    icon: 'tool-circle',
                    label: 'Circle',
                    tooltip: 'CircleROI Tool',
                    commandName: 'setToolActive',
                    commandOptions: { toolName: 'CircleROI' },
                  },
                ],
              },
            },
            {
              id: 'Zoom',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-zoom',
                label: 'Zoom',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'Zoom' },
              },
            },
            {
              id: 'WindowLevel',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-window-level',
                label: 'Window Level',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'WindowLevel' },
              },
            },
            {
              id: 'Pan',
              type: 'ohif.radioGroup',
              props: {
                type: 'tool',
                icon: 'tool-move',
                label: 'Pan',
                commandName: 'setToolActive',
                commandOptions: { toolName: 'Pan' },
              },
            },
            {
              id: 'Capture',
              type: 'ohif.action',
              props: {
                icon: 'tool-capture',
                label: 'Capture',
                tooltip: 'Capture Screenshot',
                commandName: 'showDownloadViewportModal',
              },
            },
          ],
        },
      },
      admin: {
        // Admins têm acesso total, incluindo ferramentas avançadas
        primaryToolbar: {
          id: 'primaryToolbar',
          items: [
            // Todas as ferramentas dos médicos mais algumas específicas de admin
          ],
        },
      },
    };

    return {
      ...baseCustomizations,
      ...(roleCustomizations[userRole] || roleCustomizations.technician),
    };
  }

  async initialize(studyInstanceUID, seriesInstanceUID = null) {
    try {
      // Verificar se o OHIF está disponível
      if (typeof window.OHIF === 'undefined') {
        throw new Error('OHIF Viewer not loaded');
      }

      const element = document.getElementById(this.elementId);
      if (!element) {
        throw new Error(`Element with ID ${this.elementId} not found`);
      }

      // Inicializar o viewer
      this.viewerInstance = await window.OHIF.viewer.create({
        element,
        config: this.config,
        studyInstanceUIDs: [studyInstanceUID],
        seriesInstanceUID,
      });

      this.isInitialized = true;
      return this.viewerInstance;
    } catch (error) {
      console.error('Error initializing OHIF Viewer:', error);
      throw error;
    }
  }

  buildStudyUrl(studyInstanceUID, seriesInstanceUID) {
    let url = `/viewer?StudyInstanceUIDs=${studyInstanceUID}`;
    if (seriesInstanceUID) {
      url += `&SeriesInstanceUID=${seriesInstanceUID}`;
    }
    return url;
  }

  destroy() {
    if (this.viewerInstance && this.isInitialized) {
      try {
        this.viewerInstance.destroy();
        this.viewerInstance = null;
        this.isInitialized = false;
      } catch (error) {
        console.error('Error destroying OHIF Viewer:', error);
      }
    }
  }

  // Métodos de controle do viewer
  setWindowLevel(windowWidth, windowCenter) {
    if (this.viewerInstance) {
      this.viewerInstance.setWindowLevel(windowWidth, windowCenter);
    }
  }

  resetView() {
    if (this.viewerInstance) {
      this.viewerInstance.resetView();
    }
  }

  zoomIn() {
    if (this.viewerInstance) {
      this.viewerInstance.zoomIn();
    }
  }

  zoomOut() {
    if (this.viewerInstance) {
      this.viewerInstance.zoomOut();
    }
  }

  rotate(degrees = 90) {
    if (this.viewerInstance) {
      this.viewerInstance.rotate(degrees);
    }
  }

  // Método para capturar screenshot
  captureViewport() {
    if (this.viewerInstance) {
      return this.viewerInstance.captureViewport();
    }
    return null;
  }
}

export default OHIFViewer;
